import React, { useCallback, type FC } from 'react'
import { type IntegrationServiceId } from '../../../../common/integrations'
import { ROUTER_BASEPATH } from '../../../../common/constants'
import { BannerSlot, BannerStack } from '../../../../components/BannerStack/BannerStack'
import { BANNER_PRIORITY } from '../../bannerPriority'
import { McpPromoBanners } from '../../McpPromo/McpPromoBanners'
import { McpPromoPage } from '../../McpPromo/mcpPromoConfig'
import { useCalendarOnboardingBanner, useEmailOnboardingBanner, useZoomOnboardingBanner } from './useUpcomingPageOnboardingBanners'
import { UpcomingEmptyState } from './UpcomingEmptyState/UpcomingEmptyState'
import { ZoomOnboardingBanner } from './ZoomOnboardingBanner/ZoomOnboardingBanner'
import { EmailOnboardingBanner } from './EmailOnboardingBanner/EmailOnboardingBanner'

interface UpcomingSlotProps {
  postConnectRedirectPath: (serviceId: IntegrationServiceId) => string
}

const CalendarOnboardingSlot: FC<UpcomingSlotProps> = ({ postConnectRedirectPath }) => {
  const { shouldShow, isFetching, dismiss, isDismissing, allowedCalendarIds, allowedCalendarSecondaryIds, connectedWithoutSync } = useCalendarOnboardingBanner()

  return (
    <BannerSlot id='upcomingCalendarOnboarding' priority={BANNER_PRIORITY.EMPTY_STATE} isFetching={isFetching} shouldShow={shouldShow}>
      <UpcomingEmptyState
        serviceIds={allowedCalendarIds}
        secondaryServiceIds={allowedCalendarSecondaryIds}
        connectedWithoutSync={connectedWithoutSync}
        postConnectRedirectPath={postConnectRedirectPath}
        onDismiss={dismiss}
        isDismissing={isDismissing}
      />
    </BannerSlot>
  )
}

const ZoomOnboardingSlot: FC<UpcomingSlotProps> = ({ postConnectRedirectPath }) => {
  const { shouldShow, isFetching, dismiss } = useZoomOnboardingBanner()

  return (
    <BannerSlot id='upcomingZoomOnboarding' priority={BANNER_PRIORITY.ZOOM_ONBOARDING} isFetching={isFetching} shouldShow={shouldShow}>
      <ZoomOnboardingBanner onDismiss={dismiss} postConnectRedirectPath={postConnectRedirectPath} />
    </BannerSlot>
  )
}

const EmailOnboardingSlot: FC = () => {
  const { shouldShow, isFetching, dismiss } = useEmailOnboardingBanner()

  return (
    <BannerSlot id='upcomingEmailOnboarding' priority={BANNER_PRIORITY.EMAIL_ONBOARDING} isFetching={isFetching} shouldShow={shouldShow}>
      <EmailOnboardingBanner onDismiss={dismiss} />
    </BannerSlot>
  )
}

export const UpcomingPageOnboardingBanners: React.FC = () => {
  const calendarRedirectPath = useCallback((serviceId: IntegrationServiceId) => {
    const returnTo = encodeURIComponent(window.location.pathname + window.location.search)
    return `${ROUTER_BASEPATH}/onboarding/post-connect/${serviceId}?returnTo=${returnTo}&intent=calendar`
  }, [])

  const secondaryRedirectPath = useCallback((serviceId: IntegrationServiceId) => {
    const returnTo = encodeURIComponent(window.location.pathname + window.location.search)
    return `${ROUTER_BASEPATH}/onboarding/post-connect/${serviceId}?returnTo=${returnTo}`
  }, [])

  return (
    <BannerStack>
      <CalendarOnboardingSlot postConnectRedirectPath={calendarRedirectPath} />
      <ZoomOnboardingSlot postConnectRedirectPath={secondaryRedirectPath} />
      <EmailOnboardingSlot />
      <McpPromoBanners page={McpPromoPage.UPCOMING} />
    </BannerStack>
  )
}
